import {
  MecImage,
  MecProduct,
  PDP_SWATCH_NO_COLOUR,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { getColourOptions } from '@adapters/bigcommerce/hooks/product/variantAvailability';
import {
  extractCanonicalFilenameStem,
  normalizeImageVariantPreference,
} from '@utils/imageVariantPreference';

const isImageSrc = (value: string): boolean =>
  (value.startsWith('/') && !value.startsWith('//')) ||
  value.startsWith('https://');

/**
 * The primary-slot images of colourways a shopper can actually buy.
 *
 * A colour qualifies only when it clears both of BigCommerce's separate
 * signals: some variant of it has stock (`hasStockAvailable`) and the colour
 * itself is sellable online (`availableForSaleOnline`). A compare column is an
 * invitation to buy, so leading with a shot of a sold-out or non-sellable
 * colourway misrepresents what is behind the link.
 *
 * Images not tied to a swatch -- no `swatchMatch`, or the explicit
 * `NO_COLOUR` -- picture the product rather than a variant, so no colour's
 * availability governs them and they are always kept. Products with no colours
 * at all (one-colour items, gift cards) therefore keep every primary image.
 *
 * When nothing is sellable the preference has no live colourway to point at,
 * so every primary image is kept. A sold-out product is still worth picturing
 * in a series comparison, and the alternative is a column with a blank tile.
 */
const sellablePrimaryImages = (
  product: MecProduct | null | undefined
): MecImage[] => {
  const primaryImages = (product?.images ?? []).filter(isPrimaryProductImage);

  if (!product) return primaryImages;

  const sellableColours = new Set(
    getColourOptions(product)
      .filter((colour) => colour.hasAnyStock && colour.availableForSaleOnline)
      .map((colour) => colour.name)
  );

  if (sellableColours.size === 0) return primaryImages;

  return primaryImages.filter(
    ({ swatchMatch }) =>
      !swatchMatch ||
      swatchMatch === PDP_SWATCH_NO_COLOUR ||
      sellableColours.has(swatchMatch)
  );
};

/**
 * The image a compare column will actually draw, or `undefined` when it has
 * none and would fall back to the empty placeholder.
 *
 * The authored value wins when it is already a URL. Otherwise it is read as an
 * Akeneo asset reference and matched against the canonical filename stems of
 * the product's images, so merch can name a specific shot; failing that, the
 * first image stands in. Every catalog lookup runs over
 * {@link sellablePrimaryImages}, so a reference naming a dead colourway
 * resolves to a live colourway's shot instead, unless none is live.
 *
 * The column that renders the image and the table that decides whether a
 * retail column is worth rendering both read this, so a column is never kept
 * with nothing to draw in it.
 */
export const resolveCompareImageSrc = (
  imageUrl: string | undefined,
  product: MecProduct | null | undefined
): string | undefined => {
  const authoredRef = imageUrl?.trim();

  if (authoredRef && isImageSrc(authoredRef)) return authoredRef;

  const preferredImages = normalizeImageVariantPreference(
    sellablePrimaryImages(product),
    { preferTransparent: true }
  );

  const authoredStemImage = authoredRef
    ? preferredImages.find(
        (image) =>
          extractCanonicalFilenameStem(image.urlOriginal).toLowerCase() ===
          authoredRef.toLowerCase()
      )
    : undefined;

  return authoredStemImage?.urlOriginal || preferredImages[0]?.urlOriginal;
};
